import { Users, TrendingUp, Award, UserCheck, AlertTriangle } from 'lucide-react';
import React from 'react';
import { useGetTeamSummaryQuery } from '../../services/dashboardApi';
import { WidgetProps } from './types';
import { WidgetLoadingSkeleton, WidgetEmptyState } from './WidgetErrorBoundary';

const TeamWidget: React.FC<WidgetProps> = ({ scope, projectId, size }) => {
  const { data, isLoading, error } = useGetTeamSummaryQuery(
    { projectId: scope === 'project' ? projectId : undefined },
    { pollingInterval: 300000 }
  );

  if (isLoading) {
    return <WidgetLoadingSkeleton lines={4} />;
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="text-center">
          <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
          <p className="text-red-600 text-sm font-medium">Failed to load team data</p>
          <p className="text-gray-500 text-xs mt-1">Please try refreshing</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <WidgetEmptyState
        title="No Team Data"
        description="Team information is not available"
        icon={Users}
      />
    );
  }

  const isCompact = size === '1x1';

  return (
    <div className="space-y-4">
      {/* Team Overview */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-blue-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <Users className="h-4 w-4 text-blue-600" />
            <span className="text-xs font-medium text-gray-600">Total</span>
          </div>
          <p className="text-xl font-bold text-blue-700">{data.totalMembers}</p>
        </div>
        <div className="bg-green-50 rounded-lg p-3">
          <div className="flex items-center space-x-2 mb-1">
            <UserCheck className="h-4 w-4 text-green-600" />
            <span className="text-xs font-medium text-gray-600">Active</span>
          </div>
          <p className="text-xl font-bold text-green-700">{data.activeMembers}</p>
        </div>
      </div>

      {/* Role Distribution (for larger sizes) */}
      {!isCompact && data.byRole && data.byRole.length > 0 && (
        <div className="pt-2 border-t border-gray-200">
          <p className="text-xs font-semibold text-gray-700 mb-2">By Role</p>
          <div className="space-y-2">
            {data.byRole.slice(0, 4).map((role, idx) => (
              <div key={idx} className="flex items-center justify-between">
                <span className="text-xs text-gray-600 capitalize">
                  {role.role.replace('_', ' ')}
                </span>
                <div className="flex items-center space-x-2">
                  <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-500 rounded-full"
                      style={{ width: `${(role.count / data.totalMembers) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-900 w-6 text-right">
                    {role.count}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Performers (for 2x1 and 2x2) */}
      {!isCompact && data.topPerformers && data.topPerformers.length > 0 && (
        <div className="pt-2 border-t border-gray-200">
          <div className="flex items-center space-x-2 mb-2">
            <Award className="h-4 w-4 text-yellow-500" />
            <p className="text-xs font-semibold text-gray-700">Top Performers</p>
          </div>
          <div className="space-y-2">
            {data.topPerformers.slice(0, size === '2x2' ? 5 : 3).map((performer, idx) => (
              <div key={performer.id} className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-xs font-medium">
                    {idx + 1}
                  </div>
                  {performer.avatar ? (
                    <img
                      src={performer.avatar}
                      alt={performer.name}
                      className="w-6 h-6 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 text-xs font-medium">
                      {performer.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="text-xs text-gray-900 truncate max-w-[80px]">
                    {performer.name}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-xs text-gray-500">{performer.tasksCompleted} tasks</span>
                  <div className="flex items-center text-green-600">
                    <TrendingUp className="h-3 w-3" />
                    <span className="text-xs font-medium ml-0.5">{performer.efficiency}%</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Activity Rate */}
      {size === '2x2' && (
        <div className="pt-2 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs">
            <span className="text-gray-500">Team Activity Rate</span>
            <span className="font-medium text-gray-900">
              {data.totalMembers > 0
                ? Math.round((data.activeMembers / data.totalMembers) * 100)
                : 0}
              %
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamWidget;
